import moment from 'moment';
import { defaultEnvironment } from '../environment';
import { ActivityStats, Challenge, ChallengeTemplate, Geogroup, GeogroupMember, GeogroupNewsPost, GeogroupStatement, GeogroupStats, Place, UserGeogroup, geogroupTypes, periodToParams } from '../models';
import { parseStatsDate } from '../utils/stats';
import { AppService } from './app';
import { HttpService } from './http';
import { backendJerseyTypesMap } from './jersey';
import { parsePartner } from './partner';
import { backendPublicationStatuses, publicationStatuses } from './publication-status';
import { StatsService } from './stats';
import { UserService } from './user';
export const typesMap = {
  company: 'COMPANY',
  school: 'SCHOOL',
  association: 'ASSOCIATION',
  city: 'CITY',
  private: 'PRIVATE',
  other: 'ASSOCIATION'
};
export const backendTypesMap = {
  ASSOCIATION: 'association',
  CITY: 'city',
  COMPANY: 'company',
  PRIVATE: 'private',
  OTHER: 'other',
  SCHOOL: 'school'
};
export class GeogroupService {
  static async getGeogroups({
    ordering,
    page,
    pageSize,
    parentId,
    query,
    search,
    types,
    subGroupTypes,
    onlyLinkedToPartner,
    includeAggregateInSubsidiary,
    filterContainedByAnotherSubgroupId,
    searchFields,
    closeToLocation,
    closeToRadiusFilterInMeter,
    excludeHoldingPartners
  } = {}) {
    try {
      const params = [{
        key: 'page_size',
        value: pageSize || 100
      }];
      if (ordering && (ordering !== 'distance' || closeToRadiusFilterInMeter && closeToLocation)) {
        params.push({
          key: 'ordering',
          value: ordering
        });
      }
      if (page) params.push({
        key: 'page',
        value: page
      });
      if (parentId) params.push({
        key: 'parent_id',
        value: parentId
      });
      if (query) params.push({
        key: 'query',
        value: query
      });
      if (search) params.push({
        key: 'search',
        value: search
      });
      if (searchFields) params.push({
        key: 'search_fields',
        value: searchFields.join(',')
      });
      if (includeAggregateInSubsidiary) params.push({
        key: 'annotate_contained_by_subgroup_type',
        value: backendSubGroupTypesMap['companySubsidiary']
      });
      if (filterContainedByAnotherSubgroupId) params.push({
        key: 'filter_contained_by_subgroup_id',
        value: filterContainedByAnotherSubgroupId
      });
      if (types) params.push({
        key: 'group_type[]',
        value: types.map(key => geogroupTypes.includes(key) ? typesMap[key] : backendSubGroupTypesMap[key]).join(',')
      });
      if (subGroupTypes) params.push({
        key: 'sub_group_type[]',
        value: subGroupTypes.map(key => backendSubGroupTypesMap[key]).join(',')
      });
      if (onlyLinkedToPartner !== undefined) params.push({
        key: 'linked_to_partner',
        value: !onlyLinkedToPartner
      });
      if (excludeHoldingPartners !== undefined) params.push({
        key: 'exclude_holding_partners',
        value: excludeHoldingPartners
      });
      if (closeToLocation) {
        const closeToValue = `${closeToLocation.coordinates[0]},${closeToLocation.coordinates[1]}`;
        params.push({
          key: 'close_to',
          value: closeToValue
        });
        if (closeToRadiusFilterInMeter) {
          params.push({
            key: 'close_to_radius',
            value: closeToRadiusFilterInMeter
          });
        }
      }
      const {
        count,
        next,
        results
      } = await HttpService.get('v4', `/geogroups`, params);
      const geogroups = results.map(data => parseGeogroup(data, AppService.environment.backendUrl));
      return {
        count,
        hasNext: Boolean(next),
        geogroups
      };
    } catch (err) {
      console.error('[GeogroupService][getGeogroups]', err);
      throw err;
    }
  }
  static async getGeogroup({
    geoGroupId
  }) {
    if (geoGroupId === null) throw new Error('partner has no geogroup identifier');
    try {
      const data = await HttpService.get('v4', `/geogroups/${geoGroupId}`);
      return parseGeogroup(data, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[GeogroupService][getGeogroup]', err);
      throw typeof err === 'object' && err && 'detail' in err && typeof err.detail === 'string' ? new Error(err.detail) : err;
    }
  }
  static async getAggregatedGroups({
    geoGroupId,
    partner
  }) {
    if (geoGroupId === null) throw new Error('partner has no geogroup identifier');
    try {
      const data = await HttpService.get('v4', `/geogroups/${geoGroupId}/aggregated_groups`, [], [], undefined, partner ? {
        partner: partner.code
      } : {});
      const results = Array.isArray(data) ? data : data.results;
      return results.map(props => parseGeogroup(props, AppService.environment.backendUrl));
    } catch (err) {
      console.error('[GeogroupService][getAggregatedGroups]', err);
      throw err;
    }
  }
  static async getInvitationLink(id) {
    try {
      const data = await HttpService.post('v2', `/geogroups/${id}/invitations`);
      const {
        code
      } = data;
      return {
        link: `${AppService.environment.frontendUrl}/communities/invites/${code}`,
        code
      };
    } catch (err) {
      console.error('[GeogroupService][getInvitationLink]', err);
      throw err;
    }
  }
  static async getGeogroupFromInvitationCode(code) {
    try {
      const data = await HttpService.get('v2', `/geogroups/invitations/${code}`);
      return parseGeogroup(data, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[GeogroupService][getGeogroupFromInvitationCode]', err);
      throw err;
    }
  }
  static async getUserGeogroups({
    parentId,
    statuses
  } = {}) {
    try {
      if (!UserService.currentUser) throw new Error('user not connected');
      const params = [];
      if (parentId) params.push({
        key: 'parent_id',
        value: parentId
      });
      if (statuses) {
        params.push({
          key: 'geogroup_join_type',
          value: statuses.join(',')
        });
      }
      const res = await HttpService.get('v6', `/users/${UserService.currentUser.id}/geogroups`, params);
      const geogroups = res.map(data => parseUserGeogroup(data, AppService.environment.backendUrl));
      return geogroups;
    } catch (err) {
      console.error('[GeogroupService][getUserGeogroups]', err);
      throw err;
    }
  }
  static async getUserGeogroup(id) {
    try {
      if (!UserService.currentUser) throw new Error('user not connected');
      const res = await HttpService.get('v2', `/users/${UserService.currentUser.id}/geogroups/${id}`);
      return parseUserGeogroup(res, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[GeogroupService][getUserGeogroup]', err);
      throw err;
    }
  }
  static async updateUserGeogroup(id, {
    userType: user_type
  }) {
    try {
      if (!UserService.currentUser) throw new Error('user not connected');
      const res = await HttpService.patch('v2', `/users/${UserService.currentUser.id}/geogroups/${id}`, [], [], JSON.stringify({
        user_type
      }));
      return parseUserGeogroup(res, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[GeogroupService][updateUserGeogroup]', err);
      throw err;
    }
  }
  static async addGeogroups({
    partner,
    groups
  }) {
    try {
      const body = groups.map(({
        type,
        name,
        address,
        parentId,
        subGroupType,
        siret
      }) => {
        let data = {
          type: typesMap[type],
          title: name,
          code: name.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, '_'),
          size_range: JSON.stringify({
            min: 0,
            max: 100_000_000
          }),
          automatic_challenge_creation: 'DISABLED'
        };
        if (address) data = {
          ...data,
          geo_point: JSON.stringify(address.point),
          address: address.address
        };
        if (address?.addressDetail?.countryCode) data.country_code = address.addressDetail.countryCode;
        if (parentId) data.parent = `${parentId}`;
        if (subGroupType) data.sub_group_type = backendSubGroupTypesMap[subGroupType];
        if (siret) data.siret_number = siret;
        return data;
      });
      const res = await HttpService.post('v4', `/geogroups`, [], [], JSON.stringify(body), partner ? {
        partner: partner.code
      } : {});
      return res.map(props => parseGeogroup(props, AppService.environment.backendUrl));
    } catch (err) {
      console.error('[GeogroupService][addGeogroups]', err);
      throw err;
    }
  }
  static async addGeogroup({
    type,
    name,
    areaId,
    min,
    max,
    address,
    icon,
    automaticChallengeStatus,
    bannerPicture,
    publicationStatus,
    partner,
    parentId,
    subGroupType,
    description,
    linkUrl,
    linkLabel,
    siret,
    disableAutoJoin
  }) {
    try {
      let data = {
        type: typesMap[type],
        title: name,
        code: name.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, '_'),
        size_range: JSON.stringify({
          min: min || 0,
          max: max || 100_000_000
        }),
        automatic_challenge_creation: automaticChallengeStatus ? backendAutomaticChallengeStatusMap[automaticChallengeStatus] : 'disabled'
      };
      if (areaId) data.area = `${areaId}`;
      if (address) data = {
        ...data,
        geo_point: JSON.stringify(address.point),
        address: address.address
      };
      if (address?.addressDetail?.countryCode) data.country_code = address.addressDetail.countryCode;
      if (publicationStatus === 'published') data.publication_status = 'PUBLISHED';
      if (parentId) data.parent = `${parentId}`;
      if (subGroupType) data.sub_group_type = backendSubGroupTypesMap[subGroupType];
      if (description !== undefined) data = {
        ...data,
        description
      };
      if (linkUrl !== undefined) {
        data = {
          ...data,
          link_url: linkUrl,
          link_label: linkLabel || ''
        };
      }
      if (siret !== undefined) data = {
        ...data,
        siret
      };
      const formData = new FormData();
      Object.keys(data).forEach(key => formData.append(key, data[key]));
      if (icon) formData.append('photo', icon);
      if (bannerPicture) formData.append('banner_picture', bannerPicture);
      const queryParams = [];
      if (disableAutoJoin) queryParams.push({
        key: 'no_auto_join',
        value: true
      });
      const props = await HttpService.post('v4', `/geogroups`, queryParams, [], formData, partner ? {
        partner: partner.code
      } : {});
      return parseGeogroup(props, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[GeogroupService][addGeogroup]', err);
      throw err;
    }
  }
  static async updateGeogroup(id, {
    type,
    name,
    min,
    max,
    address,
    icon,
    automaticChallengeStatus,
    bannerPicture,
    description,
    linkUrl,
    linkLabel,
    publicationStatus
  }, partner) {
    try {
      let data = {};
      if (type) data.type = typesMap[type];
      if (name) data = {
        ...data,
        title: name,
        code: name.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, '_')
      };
      if (min !== undefined || max !== undefined) {
        data.size_range = JSON.stringify({
          min: min || 0,
          max: max || 100_000_000
        });
      }
      if (automaticChallengeStatus !== undefined) data.automatic_challenge_creation = backendAutomaticChallengeStatusMap[automaticChallengeStatus];
      if (address) data = {
        ...data,
        geo_point: JSON.stringify(address.point),
        address: address.address
      };
      if (description !== undefined) data = {
        ...data,
        description
      };
      if (linkUrl !== undefined) {
        data = {
          ...data,
          link_url: linkUrl,
          link_label: linkLabel || ''
        };
      }
      if (publicationStatus) data.publication_status = backendPublicationStatuses[publicationStatus];
      const formData = new FormData();
      Object.keys(data).forEach(key => formData.append(key, data[key]));
      if (icon !== undefined) formData.append('photo', icon || '');
      if (bannerPicture !== undefined) formData.append('banner_picture', bannerPicture || '');
      const props = await HttpService.patch('v4', `/geogroups/${id}`, [], [], formData, partner ? {
        partner: partner.code
      } : undefined);
      return parseGeogroup(props, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[GeogroupService][updateGeogroup]', err);
      throw err;
    }
  }
  static async removeGeogroup(id, partner) {
    try {
      if (!UserService.currentUser) throw new Error('user not connected');
      await HttpService.delete('v4', `/geogroups/${id}`, [], [], null, partner?.code ? {
        partner: partner.code
      } : undefined);
    } catch (err) {
      console.error('[GeogroupService][removeGeogroup]', err);
      throw err;
    }
  }
  static async joinGeogroup(id, {
    disabledRetrieval,
    userId,
    partnerCode,
    firstName,
    lastName,
    professionalEmail
  } = {}) {
    try {
      if (!userId && !UserService.currentUser) throw new Error('user not connected');
      const data = await HttpService.post('v6', `/users/${userId || UserService.currentUser?.id}/geogroups`, [], [], JSON.stringify({
        group: id,
        ...(firstName ? {
          first_name: firstName
        } : {}),
        ...(lastName ? {
          last_name: lastName
        } : {}),
        ...(professionalEmail ? {
          professional_email: professionalEmail
        } : {})
      }), partnerCode ? {
        partner: partnerCode
      } : undefined);
      if (!disabledRetrieval && typeof data.group === 'number') {
        data.group = await HttpService.get('v4', `/geogroups/${id}`);
      }
      return parseUserGeogroup(data, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[GeogroupService][joinGeogroup]', err);
      throw err;
    }
  }
  static async joinGeogroupWithInvitation(code, {
    geogroupId
  } = {}) {
    try {
      if (!UserService.currentUser) throw new Error('user not connected');
      const data = await HttpService.post('v2', `/users/${UserService.currentUser.id}/geogroups/invitations/${code}`, geogroupId ? [{
        key: 'group',
        value: geogroupId
      }] : []);
      return parseUserGeogroup(data, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[GeogroupService][joinGeogroupWithInvitation]', err);
      return null;
    }
  }
  static async leaveGeogroup(id, {
    userId,
    partnerCode
  } = {}) {
    try {
      if (!userId && !UserService.currentUser) throw new Error('user not connected');
      await HttpService.delete('v2', `/users/${userId || UserService.currentUser?.id}/geogroups/${id}`, [], [], null, partnerCode ? {
        partner: partnerCode
      } : undefined);
    } catch (err) {
      console.error('[GeogroupService][leaveGeogroup]', err);
      throw err;
    }
  }
  static async getMembers(geoGroupId, {
    partner,
    page,
    pageSize,
    orderBy,
    order,
    registrationStartDate,
    registrationEndDate,
    onlyNew,
    joinTypes,
    userType
  }) {
    try {
      const params = [];
      if (orderBy && order) {
        let value;
        if (orderBy === 'username') value = 'user__username';else if (orderBy === 'joinDate') value = 'join_date';else if (orderBy === 'distance') value = 'meters';
        if (value) {
          if (order === 'desc') value = `-${value}`;
          params.push({
            key: 'ordering',
            value
          });
        }
      }
      if (registrationStartDate || registrationEndDate) {
        if (registrationStartDate) params.push({
          key: 'date_start',
          value: registrationStartDate.format('YYYY-MM-DD')
        });
        if (registrationEndDate) params.push({
          key: 'date_end',
          value: registrationEndDate.format('YYYY-MM-DD')
        });
      } else if (onlyNew) params.push({
        key: 'only_new',
        value: true
      });
      if (joinTypes) {
        params.push(...joinTypes.map(key => ({
          key: 'join_type[]',
          value: backendUserGeogroupStatuses[key]
        })));
      }
      if (userType) {
        params.push({
          key: 'user_type',
          value: userType
        });
      }
      const data = await HttpService.get('v3', `/geogroups/${geoGroupId}/users`, [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, ...params], [], null, {
        partner: partner?.code
      });
      const users = data.results.reduce((res, userData) => {
        const user = parseGeogroupMember(userData);
        if (user) res.push(user);
        return res;
      }, []);
      return {
        count: data.count,
        users,
        hasNext: Boolean(data.next)
      };
    } catch (err) {
      console.error('[GeogroupService][getMembers]', err);
      throw err;
    }
  }
  static async getMembersCount(geoGroupId, {
    partner,
    registrationStartDate,
    registrationEndDate,
    joinTypes
  }) {
    try {
      const params = [];
      if (registrationStartDate || registrationEndDate) {
        if (registrationStartDate) params.push({
          key: 'date_start',
          value: registrationStartDate.format('YYYY-MM-DD')
        });
        if (registrationEndDate) params.push({
          key: 'date_end',
          value: registrationEndDate.format('YYYY-MM-DD')
        });
      }
      if (joinTypes) {
        params.push(...joinTypes.map(key => ({
          key: 'join_type[]',
          value: backendUserGeogroupStatuses[key]
        })));
      }
      const {
        count
      } = await HttpService.get('v2', `/geogroups/${geoGroupId}/users_count`, params, [], null, {
        partner: partner?.code
      });
      return {
        count
      };
    } catch (err) {
      console.error('[GeogroupService][getMembersCount]', err);
      throw err;
    }
  }
  static async removeMember({
    geoGroupId
  }, {
    id
  }) {
    try {
      await HttpService.delete('v2', `/geogroups/${geoGroupId}/users/${id}`);
      return true;
    } catch (err) {
      console.error('[GeogroupService][removeMember]', err);
      throw err;
    }
  }
  static async getChallenges(geoGroupId, {
    period,
    page,
    pageSize,
    query,
    state,
    order,
    retrieveSubgroupsChallenges,
    retrieveOneChallengePerEvent,
    sites
  }) {
    try {
      const params = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, {
        key: 'target_type[]',
        value: 'TRAVELED_DISTANCE'
      }, {
        key: 'target_type[]',
        value: 'CYCLING_DAYS'
      }, {
        key: 'target_type[]',
        value: 'TRACES_COUNT'
      }, {
        key: 'target_type[]',
        value: 'NO_TARGET'
      }, {
        key: 'target_type[]',
        value: 'BTTF_POINTS'
      }, {
        key: 'target_type[]',
        value: 'CUSTOM'
      }];
      if (query) params.push({
        key: 'query',
        value: query
      });
      if (period?.type === 'custom') params.push(...periodToParams(period.toIPeriod()));
      if (state) state.forEach(value => params.push({
        key: 'state[]',
        value
      }));
      if (order) params.push({
        key: 'ordering',
        value: order
      });
      if (retrieveSubgroupsChallenges) params.push({
        key: 'add_sub_group_challenge',
        value: true
      });
      if (retrieveOneChallengePerEvent) params.push({
        key: 'one_challenge_per_event',
        value: true
      });
      const {
        count,
        next,
        results
      } = await HttpService.get('v5', `/geogroups/${geoGroupId}/challenges`, params);
      const challenges = results.reduce((res, data) => {
        const challenge = parseChallenge(data, AppService.environment.backendUrl, sites);
        if (challenge) res.push(challenge);
        return res;
      }, []);
      return {
        challenges,
        count,
        hasNext: Boolean(next)
      };
    } catch (err) {
      console.error('[GeogroupService][getChallenges]', err);
      throw err;
    }
  }
  static async getChallenge(geoGroupId, challengeId, sites) {
    try {
      const data = await HttpService.get('v5', `/geogroups/${geoGroupId}/challenges/${challengeId}`);
      const challenge = parseChallenge(data, AppService.environment.backendUrl, sites);
      if (!challenge) throw new Error('challenge not published or challenge type not taken into account');
      return challenge;
    } catch (err) {
      console.error('[GeogroupService][getChallenge]', err);
      throw err;
    }
  }
  static async addChallenge({
    code,
    geoGroupId
  }, props, photo, {
    expectedStatus
  } = {}) {
    try {
      if (!geoGroupId) throw new Error('invalid geogroupId');
      if (!props.event && !props.collaboration_type) props.collaboration_type = 'EVERYONE';
      let body;
      if (photo) {
        body = new FormData();
        body.append('photo', photo);
        body.append('data', JSON.stringify(props));
      } else {
        body = JSON.stringify(props);
      }
      const result = await HttpService.post('v5', `/geogroups/${geoGroupId}/challenges`, [], [], body, code ? {
        partner: code,
        expectedStatus
      } : undefined);
      const challenge = parseChallenge(result, AppService.environment.backendUrl);
      if (!challenge) throw new Error('challenge not published or challenge type not taken into account');
      return challenge;
    } catch (err) {
      console.error('[GeogroupService][addChallenge]', err);
      throw err;
    }
  }
  static async updateChallenge({
    code,
    geoGroupId
  }, props, photo) {
    try {
      if (!geoGroupId) throw new Error('partner has no geogroup');
      let body;
      if (photo || photo === null) {
        body = new FormData();
        body.append('photo', photo || '');
        body.append('data', JSON.stringify(props));
      } else {
        body = JSON.stringify(props);
      }
      const data = await HttpService.put('v4', `/geogroups/${geoGroupId}/challenges/${props.id}`, [], [], body, {
        partner: code
      });
      const challenge = parseChallenge(data, AppService.environment.backendUrl);
      if (!challenge) throw new Error('challenge not published or challenge type not taken into account');
      return challenge;
    } catch (err) {
      console.error('[GeogroupService][updateChallenge]', err);
      throw err;
    }
  }
  static async removeChallenge({
    code,
    geoGroupId
  }, id) {
    try {
      await HttpService.delete('v4', `/geogroups/${geoGroupId}/challenges/${id}`, [], [], null, {
        partner: code
      });
      return true;
    } catch (err) {
      console.error('[GeogroupService][removeChallenge]', err);
      throw err;
    }
  }
  static async getSubGroupAggregationRules(mainGroupId) {
    try {
      const {
        results
      } = await HttpService.get('v2', '/sub_group_aggregation_rules', [{
        key: 'main_group',
        value: mainGroupId
      }, {
        key: 'page_size',
        value: 500
      }]);
      return results.map(parseSubGroupAggregationRule);
    } catch (err) {
      console.error('[GeogroupService][getSubGroupAggregationRules]', err);
      throw err;
    }
  }
  static async getSubGroupAggregationRule(id) {
    try {
      const result = await HttpService.get('v2', `/sub_group_aggregation_rules/${id}`);
      return parseSubGroupAggregationRule(result);
    } catch (err) {
      console.error('[GeogroupService][getSubGroupAggregationRule]', err);
      throw err;
    }
  }
  static async createSubGroupAggregationRule(mainGroupId, targetSubGroupId, sourceSubGroupType, sourceSubGroupIds) {
    try {
      const result = await HttpService.post('v2', '/sub_group_aggregation_rules', [], [], JSON.stringify({
        main_group: mainGroupId,
        target_sub_group: targetSubGroupId,
        source_sub_group_type: sourceSubGroupType,
        source_sub_group_ids: sourceSubGroupIds
      }));
      return parseSubGroupAggregationRule(result);
    } catch (err) {
      console.error('[GeogroupService][createSubGroupAggregationRule]', err);
      throw err;
    }
  }
  static async updateSubGroupAggregationRule(id, targetSubGroupId, sourceSubGroupType, sourceSubGroupIds) {
    try {
      const result = await HttpService.patch('v2', `/sub_group_aggregation_rules/${id}`, [], [], JSON.stringify({
        target_sub_group: targetSubGroupId,
        source_sub_group_type: sourceSubGroupType,
        source_sub_group_ids: sourceSubGroupIds
      }));
      return parseSubGroupAggregationRule(result);
    } catch (err) {
      console.error('[GeogroupService][updateSubGroupAggregationRule]', err);
      throw err;
    }
  }
  static async deleteSubGroupAggregationRule(id) {
    try {
      await HttpService.delete('v2', `/sub_group_aggregation_rules/${id}`);
    } catch (err) {
      console.error('[GeogroupService][deleteSubGroupAggregationRule]', err);
      throw err;
    }
  }
  static async getSubGroupsWithoutAggregationRule(mainGroupId, sourceSubGroupType, targetSubGroupType) {
    try {
      const params = [{
        key: 'source_sub_group_type',
        value: backendSubGroupTypesMap[sourceSubGroupType]
      }, {
        key: 'target_sub_group_type',
        value: backendSubGroupTypesMap[targetSubGroupType]
      }];
      const results = await HttpService.get('v2', `/geogroups/${mainGroupId}/sub_groups_without_aggregation_rule`, params);
      return results.map(props => parseGeogroup(props, AppService.environment.backendUrl));
    } catch (err) {
      console.error('[GeogroupService][getSubGroupsWithoutAggregationRule]', err);
      throw err;
    }
  }
  static async unpublishChallenge(geoGroupId, challengeId) {
    try {
      await HttpService.patch('v5', `/geogroups/${geoGroupId}/challenges/${challengeId}`, [], [], JSON.stringify({
        publication_status: 'UNPUBLISHED'
      }));
    } catch (err) {
      console.error('[GeogroupService][unpublishChallenge]', err);
      throw err;
    }
  }
  static async getChallengeLeaderboard(challengeId, {
    page,
    pageSize,
    subgroup,
    userId,
    addEmployeeData = true,
    rankField
  }) {
    try {
      const params = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }];
      if (subgroup) params.push({
        key: 'subgroup_id',
        value: subgroup.id
      });
      if (userId) params.push({
        key: 'user_id',
        value: userId
      });
      if (rankField && rankField !== 'progress_value') params.push({
        key: 'rank_field',
        value: rankField
      });
      params.push({
        key: 'add_employee_data',
        value: addEmployeeData
      });
      const {
        results,
        next,
        count
      } = await HttpService.get('v5', `/challenges/${challengeId}/leaderboard`, params);
      const leaderboard = results.map(({
        user_id: id,
        username,
        firstname: firstName,
        lastname: lastName,
        professional_email: professionalEmail,
        rank,
        leaderboard_index: leaderboardIndex,
        progress_value: progressValue,
        jerseys,
        subgroups,
        profile_picture
      }) => ({
        id,
        username: username || null,
        firstName,
        lastName,
        professionalEmail,
        rank,
        leaderboardIndex,
        progressValue,
        jerseys: jerseys?.map(type => backendJerseyTypesMap[type]) || [],
        subGroups: subgroups?.map(({
          id,
          sub_group_type,
          title
        }) => ({
          id,
          type: subGroupTypesMap[sub_group_type],
          title
        })) || [],
        profilePicture: profile_picture ? `${defaultEnvironment.backendUrl}${profile_picture}` : profile_picture
      }));
      return {
        count,
        leaderboard,
        hasMore: Boolean(next)
      };
    } catch (err) {
      console.error('[GeogroupService][getChallengeLeaderboard]', err);
      throw err;
    }
  }
  static async getChallengeLeaderboardBySubGroups({
    code: partner
  }, challengeId, {
    subGroupDisplay
  }) {
    try {
      const results = await HttpService.get('v2', `/challenges/${challengeId}/subgroup_leaderboard`, [{
        key: 'sub_group_type',
        value: subGroupDisplay && backendSubGroupTypesMap[subGroupDisplay] || 'COMPANY_DEPARTMENT'
      }], [], null, {
        partner
      });
      const leaderboard = results.map(({
        id,
        title,
        rank,
        progress_value: progressValue,
        user_count_with_not_null_progress: participants
      }) => ({
        id,
        username: title || '',
        rank,
        progressValue,
        participants
      }));
      return leaderboard;
    } catch (err) {
      console.error('[GeogroupService][getChallengeLeaderboardBySubGroups]', err);
      throw err;
    }
  }
  static async getLeaderboardBySubGroups({
    code: partner,
    geoGroupId
  }, {
    jersey,
    subGroupDisplay,
    page,
    pageSize
  }) {
    try {
      const {
        count,
        results
      } = await HttpService.get('v3', `/geogroups/${geoGroupId}/subgroup_windowed_activity_leaderboard`, [{
        key: 'leaderboard_type',
        value: jersey === 'distance' ? 'distance' : 'count_commuting_trace'
      }, {
        key: 'sub_group_type',
        value: subGroupDisplay && backendSubGroupTypesMap[subGroupDisplay] || 'COMPANY_DEPARTMENT'
      }, {
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }], [], null, {
        partner
      });
      const items = results.map(({
        id,
        rank,
        leaderboard_index: leaderboardIndex,
        title,
        progress_value: progressValue,
        counter_active_user: nbMembers,
        counter_active_user_with_home_work_trace: nbMembersWithHomeWorkTrace
      }) => ({
        type: 'distance',
        id,
        rank,
        leaderboardIndex,
        progressValue,
        group: {
          id,
          title,
          nbMembers,
          nbMembersWithHomeWorkTrace
        }
      }));
      return {
        count,
        items
      };
    } catch (err) {
      console.error('[GeogroupService][getLeaderboardBySubGroups]', err);
      throw err;
    }
  }
  static async sendEmailToParticipants(challengeId, email, usersIds, body) {
    try {
      const params = {
        users_ids: usersIds,
        contact_email: email
      };
      if (body) params.email_body = body;
      await HttpService.post('v2', `/challenges/${challengeId}/send-contact-email`, [], [], JSON.stringify(params));
    } catch (err) {
      console.error('[GeogroupService][sendEmailToParticipants]', err);
      throw err;
    }
  }
  static async getNewsPosts(geoGroupId, {
    ordering,
    page,
    pageSize
  }) {
    try {
      const {
        count,
        next,
        results
      } = await HttpService.get('v5', `/geogroups/${geoGroupId}/news`, [{
        key: 'ordering',
        value: ordering
      }, {
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }]);
      return {
        count,
        hasNext: Boolean(next),
        results: results.map(parseNewsPost)
      };
    } catch (err) {
      console.error('[GeogroupService][getNewsPosts]', err);
      throw err;
    }
  }
  static async getNewsPost({
    partner,
    geoGroupId,
    newsPostId
  }) {
    try {
      const data = await HttpService.get('v5', `/geogroups/${geoGroupId}/news/${newsPostId}`, [], [], null, {
        partner: partner.code
      });
      return parseNewsPost(data);
    } catch (err) {
      console.error('[GeogroupService][getNewsPost]', err);
      throw err;
    }
  }
  static async addNewsPost(geoGroupId, {
    author_type,
    text,
    photo,
    geo_group
  }, partner) {
    try {
      const props = {
        author_type,
        text: formatNewsPostContent(text),
        photo,
        geo_group
      };
      const formData = new FormData();
      Object.keys(props).forEach(key => {
        const value = props[key];
        if (value !== undefined) {
          if (typeof value === 'number') formData.append(key, `${value}`);else formData.append(key, value);
        }
      });
      const data = await HttpService.post('v5', `/geogroups/${geoGroupId}/news`, [], [], formData, partner ? {
        partner: partner.code
      } : undefined);
      return parseNewsPost(data);
    } catch (err) {
      console.error('[GeogroupService][addNewsPost]', err);
      throw err;
    }
  }
  static async updateNewsPost(geoGroupId, id, {
    author_type,
    text,
    photo
  }, partner) {
    try {
      const props = {
        author_type,
        text: formatNewsPostContent(text),
        photo
      };
      let body;
      if (props.photo) {
        const formData = new FormData();
        Object.keys(props).forEach(key => {
          const value = props[key];
          if (value) formData.append(key, value);
        });
        body = formData;
      } else {
        body = JSON.stringify(props);
      }
      const data = await HttpService.patch('v5', `/geogroups/${geoGroupId}/news/${id}`, [], [], body, partner ? {
        partner: partner.code
      } : undefined);
      return parseNewsPost(data);
    } catch (err) {
      console.error('[GeogroupService][updateNewsPost]', err);
      throw err;
    }
  }
  static async removeNewsPost(geoGroupId, {
    id
  }, partner) {
    try {
      await HttpService.delete('v5', `/geogroups/${geoGroupId}/news/${id}`, [], [], null, partner ? {
        partner: partner.code
      } : undefined);
      return true;
    } catch (err) {
      console.error('[GeogroupService][removeNewsPost]', err);
      throw err;
    }
  }
  static async getStats({
    geoGroupId,
    period
  }) {
    try {
      const data = await HttpService.get('v2', `/geogroups/${geoGroupId}/stats`, [...periodToParams(period)]);
      const stats = period.unit === 'all_period' ? new GeogroupStats(data[0]?.count_manually_joined_members || 0, []) : parseGeogroupStats(data);
      return stats;
    } catch (err) {
      console.error('[GeogroupService][getStats]', err);
      throw err;
    }
  }
  static async getActivityStats({
    geoGroupId,
    area
  }, period) {
    if (geoGroupId === null) throw new Error('partner has no geogroup identifier');
    try {
      const [data, computedRouteStats] = await Promise.all([HttpService.get('v2', `/geogroups/${geoGroupId}/activities`, [...periodToParams(period)]), area ? StatsService.getComputedRouteStats(period) : null]);
      if (period.unit === 'all_period') {
        const {
          counter_distance: totalDistance,
          counter_home_work_reference_trips_distance: totalHomeWorkDistance,
          counter_duration: totalDuration,
          counter_home_work_reference_trips_duration: totalHomeWorkDuration,
          counter_traces: totalNbRoutes,
          counter_home_work_reference_trips_traces: totalHomeWorkJourneys,
          counter_active_user: nbActiveUsers,
          counter_active_user_with_home_work_trace: nbActiveUsersWithHomeWorkTrace,
          counter_home_work_reference_trips_co2_saved: totalHomeWorkSavedCO2InKg
        } = data[0];
        return new ActivityStats(computedRouteStats?.count || 0, totalDistance, totalDuration, totalNbRoutes, [], totalHomeWorkDistance, totalHomeWorkDuration, totalHomeWorkJourneys, totalHomeWorkSavedCO2InKg, nbActiveUsers, nbActiveUsersWithHomeWorkTrace);
      }
      data.sort((a, b) => a.activity_day.localeCompare(b.activity_day));
      const stats = parseActivityStats(data, computedRouteStats);
      return stats;
    } catch (err) {
      console.error('[GeogroupService][getActivityStats]', err);
      throw err;
    }
  }
  static async getModalShareOfCycling({
    partner
  }) {
    try {
      const {
        active_count: activeCount,
        bike_modal_share: percentage,
        commuter_count: commutersCount
      } = await HttpService.get('v1', `/stats/partner/${partner.id}/commuter_from_population_stats`);
      return {
        commutersCount,
        activeCount,
        percentage
      };
    } catch (err) {
      console.error('[GeogroupService][getModalShareOfCycling]', err);
      throw err;
    }
  }
  static async getSubgroupsStats({
    geoGroupId,
    subgroupType,
    period,
    limit,
    ordering
  }) {
    try {
      const params = [{
        key: 'sub_group_type',
        value: backendSubGroupTypesMap[subgroupType]
      }, {
        key: 'ordering',
        value: ordering
      }, ...periodToParams(period)];
      if (limit) params.push({
        key: 'limit',
        value: limit
      });
      const data = await HttpService.get('v2', `/geogroups/${geoGroupId}/sub_group_aggregated_stats`, params);
      return data.map(parseGeogroupSubgroupsStats);
    } catch (err) {
      console.error('[GeogroupService][getSubgroupsStats]', err);
      throw err;
    }
  }
  static async getAverageJourneyStats({
    geoGroupId,
    period,
    journeysType
  }) {
    try {
      const data = await HttpService.get('v2', `/geogroups/${geoGroupId}/average_trace_stats`, [{
        key: 'only_home_work_traces',
        value: journeysType === 'homeWork'
      }, ...periodToParams(period)]);
      return parseAverageJourneyStats(data);
    } catch (err) {
      console.error('[GeogroupService][getAverageJourneyStats]', err);
      throw err;
    }
  }
  static async getStatements({
    event,
    groupId
  }) {
    const {
      startDate,
      endDate
    } = event;
    const strStartDate = startDate.toISOString().split('T')[0].split('-').reverse().join('-');
    const strEndDate = endDate.toISOString().split('T')[0].split('-').reverse().join('-');
    try {
      const {
        results
      } = await HttpService.get('v2', `/geogroups/${groupId}/manual_activities`, [{
        key: 'period',
        value: 'custom'
      }, {
        key: 'date_start',
        value: strStartDate
      }, {
        key: 'date_end',
        value: strEndDate
      }]);
      const statements = results.map(parseGeogroupStatement);
      return statements;
    } catch (err) {
      console.error('[GeogroupService][getStatements]', err);
      throw err;
    }
  }
  static async addStatement({
    groupId,
    strDate: activity_day,
    distance: counter_distance,
    nbTraces: counter_traces
  }) {
    try {
      const data = await HttpService.post('v2', `/geogroups/${groupId}/manual_activities`, [], [], JSON.stringify({
        activity_day,
        counter_distance,
        counter_traces
      }));
      const statement = parseGeogroupStatement(data);
      return statement;
    } catch (err) {
      console.error('[GeogroupService][addStatement]', err);
      throw err;
    }
  }
  static async updateStatement(id, {
    groupId,
    strDate: activity_day,
    distance: counter_distance,
    nbTraces: counter_traces
  }) {
    try {
      const data = await HttpService.patch('v2', `/geogroups/${groupId}/manual_activities/${id}`, [], [], JSON.stringify({
        activity_day,
        counter_distance,
        counter_traces
      }));
      const statement = parseGeogroupStatement(data);
      return statement;
    } catch (err) {
      console.error('[GeogroupService][updateStatement]', err);
      throw err;
    }
  }
  static async likeNewsPost({
    newsPostId
  }) {
    try {
      await HttpService.put('v2', `/geo_group_news/${newsPostId}/like`);
      return true;
    } catch (err) {
      console.error('[GeogroupService][likeNewsPost]', err);
      throw err;
    }
  }
  static async unlikeNewsPost({
    newsPostId
  }) {
    try {
      await HttpService.delete('v2', `/geo_group_news/${newsPostId}/like`);
      return true;
    } catch (err) {
      console.error('[GeogroupService][unlikeNewsPost]', err);
      throw err;
    }
  }
  static async getSubgroupTypes({
    groupId
  }) {
    try {
      const data = await HttpService.get('v1', `/geogroups/${groupId}/sub_group_types`);
      return data.map(key => subGroupTypesMap[key]).filter(Boolean);
    } catch (err) {
      console.error('[GeogroupService][getSubgroupTypes]', err);
      throw err;
    }
  }
  static async askToJoinEvent({
    geoGroupId,
    eventId
  }) {
    try {
      await HttpService.post('v2', `/geogroups/${geoGroupId}/request_to_participate_event`, [], [], JSON.stringify({
        event_id: eventId
      }));
      return true;
    } catch (err) {
      console.error('[GeogroupService][askToJoinEvent]', err);
      throw err;
    }
  }
}
export class SubGroupAggregationRule {
  constructor(id, mainGroupId, targetSubGroupId, sourceSubGroupType, sourceSubGroupIds) {
    this.id = id;
    this.mainGroupId = mainGroupId;
    this.targetSubGroupId = targetSubGroupId;
    this.sourceSubGroupType = sourceSubGroupType;
    this.sourceSubGroupIds = sourceSubGroupIds;
  }
}
export function parseSubGroupAggregationRule(data) {
  return new SubGroupAggregationRule(data.id, data.main_group, data.target_sub_group, data.source_sub_group_type, data.source_sub_group_ids);
}
export const backendSubGroupTypesMap = {
  companyTeam: 'COMPANY_DEPARTMENT',
  companySite: 'COMPANY_SITE',
  companySubsidiary: 'COMPANY_SUBSIDIARY',
  companyRegion: 'COMPANY_REGION'
};
export const subGroupTypesMap = {
  COMPANY_DEPARTMENT: 'companyTeam',
  COMPANY_SITE: 'companySite',
  COMPANY_SUBSIDIARY: 'companySubsidiary',
  COMPANY_REGION: 'companyRegion'
};
const automaticChallengeStatusMap = {
  AUTOMATIC_DISTANCE: 'automaticDistance',
  DISABLED: 'disabled',
  EDITORIALIZED: 'editorialized'
};
const backendAutomaticChallengeStatusMap = {
  automaticDistance: 'AUTOMATIC_DISTANCE',
  disabled: 'DISABLED',
  editorialized: 'EDITORIALIZED'
};
export function parseGeogroup({
  id,
  type,
  area,
  title,
  code,
  photo,
  geo_point,
  total_members,
  total_distance,
  automatic_challenge_creation,
  size_range,
  address,
  parent,
  sub_group_type,
  partner,
  publication_status,
  banner_picture,
  description,
  link_label,
  link_url,
  open_membership,
  contained_by_subgroup_id,
  contained_by_subgroup_title,
  holding_group,
  can_create_challenge_from_event
}, backendUrl) {
  return new Geogroup(id, backendTypesMap[type], area, title || '', code, photo ? `${backendUrl}${photo}` : null, geo_point ? new Place(id, geo_point, address ? sub_group_type === 'COMPANY_SITE' ? {
    primaryText: title || '',
    secondaryText: address
  } : {
    primaryText: address
  } : undefined) : null, total_members, total_distance, automatic_challenge_creation ? automaticChallengeStatusMap[automatic_challenge_creation] : 'disabled', code === 'lyon', size_range, parent, sub_group_type ? subGroupTypesMap[sub_group_type] : sub_group_type, partner ? parsePartner({
    partner
  }) : partner, publication_status ? publicationStatuses[publication_status] : undefined, banner_picture ? `${backendUrl}${banner_picture}` : undefined, description, link_url ? {
    url: link_url,
    label: link_label || null
  } : null, Boolean(open_membership), contained_by_subgroup_id, contained_by_subgroup_title, holding_group ? {
    id: holding_group.id,
    title: holding_group.title,
    photo: holding_group.photo ? `${backendUrl}${holding_group.photo}` : holding_group.photo
  } : null, can_create_challenge_from_event);
}
const userGeogroupStatuses = {
  AUTOMATICALLY_JOIN: 'automaticallyJoin',
  AUTOMATICALLY_QUIT: 'automaticallyQuit',
  MANUALLY_JOIN: 'manuallyJoin',
  MANUALLY_QUIT: 'manuallyQuit',
  WAITING_FOR_APPROVAL: 'waitingForApproval'
};
const backendUserGeogroupStatuses = {
  automaticallyJoin: 'AUTOMATICALLY_JOIN',
  automaticallyQuit: 'AUTOMATICALLY_QUIT',
  manuallyJoin: 'MANUALLY_JOIN',
  manuallyQuit: 'MANUALLY_QUIT',
  waitingForApproval: 'WAITING_FOR_APPROVAL'
};
function parseUserGeogroup({
  type,
  group,
  join_date,
  last_activity_date,
  automatic_challenge_creation,
  user_type
}, backendUrl) {
  return new UserGeogroup(userGeogroupStatuses[type], parseGeogroup(group, backendUrl), moment(join_date), moment(last_activity_date), automatic_challenge_creation ? automaticChallengeStatusMap[automatic_challenge_creation] : 'disabled', user_type === 'ADMIN');
}
function parseGeogroupMember({
  id,
  username,
  join_date,
  last_activity_date,
  meters
}) {
  if (!id) return null;
  return new GeogroupMember(id, username, moment(join_date), moment(last_activity_date), meters || 0);
}
const challengeTypes = {
  TRAVELED_DISTANCE: 'traveledDistance',
  CYCLING_DAYS: 'cyclingDays',
  NO_TARGET: 'noTarget',
  BTTF_POINTS: 'bttf',
  CUSTOM: 'custom'
};
export const backendChallengeTypes = {
  traveledDistance: 'TRAVELED_DISTANCE',
  cyclingDays: 'CYCLING_DAYS',
  homeWorkJourneysCount: 'TRACES_COUNT',
  journeysCount: 'TRACES_COUNT',
  noTarget: 'NO_TARGET',
  bttf: 'BTTF_POINTS',
  custom: 'CUSTOM'
};
const collaborationTypes = {
  INDIVIDUAL: 'individual',
  EVERYONE: 'global'
};
const traceTypes = {
  ALL: 'all',
  COMMUTING: 'commuting',
  NOT_APPLICABLE: 'notApplicable'
};
export function parseChallenge({
  id,
  target_type,
  collaboration_type,
  trace_type,
  start_datetime,
  end_datetime,
  title,
  description,
  photo,
  details_url,
  total_members,
  progress_value,
  target_value,
  publication_status,
  event,
  default_sub_group_display,
  event_automatic_subscription,
  group,
  certified,
  metrics_weights,
  common_progress_weighting_details,
  summary_video,
  covered_by_paid_contract
}, backendUrl, sites) {
  if (publication_status !== 'PUBLISHED') return null;
  let type = challengeTypes[target_type];
  if (target_type === 'TRACES_COUNT') {
    if (trace_type === 'COMMUTING') type = 'homeWorkJourneysCount';else if (trace_type === 'ALL') type = 'journeysCount';
  }
  if (!type) return null;
  return new Challenge(id, type, default_sub_group_display && ['COMPANY_DEPARTMENT', 'COMPANY_SITE'].includes(default_sub_group_display) ? 'teams' : collaborationTypes[collaboration_type], traceTypes[trace_type], moment(start_datetime), moment(end_datetime), title || `Challenge #${id}`, description, photo ? `${backendUrl}${photo}` : null, details_url, total_members, progress_value, target_value || 0, publicationStatuses[publication_status], typeof event === 'object' ? event?.id || null : event, default_sub_group_display && subGroupTypesMap[default_sub_group_display] || null, group, Boolean(sites?.find(({
    id
  }) => id === group)), typeof event === 'object' && event?.sponsor || null, certified, event_automatic_subscription, metrics_weights, common_progress_weighting_details ? {
    declaredEmployeeCount: common_progress_weighting_details.declared_employee_count
  } : null, summary_video ? summary_video.indexOf('https') === -1 && summary_video.indexOf('http') === -1 ? `${backendUrl}${summary_video}` : summary_video : null, covered_by_paid_contract);
}
export function parseChallengeTemplate(eventId, {
  id,
  target_type,
  collaboration_type,
  trace_type,
  start_datetime,
  end_datetime,
  title,
  description,
  photo,
  details_url,
  target_value,
  default_sub_group_display,
  metrics_weights,
  progress_weighting_mode
}, backendUrl) {
  let type = challengeTypes[target_type];
  if (target_type === 'TRACES_COUNT') {
    if (trace_type === 'COMMUTING') type = 'homeWorkJourneysCount';else if (trace_type === 'ALL') type = 'journeysCount';
  }
  if (!type) return null;
  return new ChallengeTemplate(id, type, default_sub_group_display && ['COMPANY_DEPARTMENT', 'COMPANY_SITE'].includes(default_sub_group_display) ? 'teams' : collaborationTypes[collaboration_type], traceTypes[trace_type], moment(start_datetime), moment(end_datetime), title || `Challenge #${id}`, description, photo ? photo.indexOf('https') === -1 && photo.indexOf('http') === -1 ? `${backendUrl}${photo}` : photo : null, details_url, target_value || 0, eventId, default_sub_group_display && subGroupTypesMap[default_sub_group_display] || null, undefined,
  // group
  undefined,
  // isLocal
  undefined,
  // sponsor
  metrics_weights, progress_weighting_mode ?? null);
}
function parseNewsPost({
  id,
  geo_group,
  creator,
  author_type,
  created,
  text,
  photo,
  link_label,
  link_url,
  publication_date,
  type,
  likes_count,
  liked
}) {
  const typesMap = {
    CHALLENGE_END: 'challengeEnd',
    CHALLENGE_OBJECTIVE_100: 'challengeObjective100',
    CHALLENGE_OBJECTIVE_25: 'challengeObjective25',
    CHALLENGE_OBJECTIVE_50: 'challengeObjective50',
    CHALLENGE_OBJECTIVE_75: 'challengeObjective75',
    CHALLENGE_START: 'challengeStart',
    CHALLENGE_TOP_RANKING: 'challengeTopRanking',
    MANUAL: 'manual'
  };
  return new GeogroupNewsPost(id, geo_group ? {
    id: geo_group.id,
    photo: geo_group.photo ? `${defaultEnvironment.backendUrl}${geo_group.photo}` : geo_group.photo,
    title: geo_group.title
  } : null, creator ? {
    id: creator.id,
    profilePicture: creator.profile_picture ? `${defaultEnvironment.backendUrl}${creator.profile_picture}` : creator.profile_picture,
    username: creator.username
  } : null, author_type === 'AUTHOR_AS_GROUP', moment(created), text, photo ? photo.url : null, link_url ? {
    url: link_url,
    label: link_label
  } : null, publication_date ? moment(publication_date) : null, type && typesMap[type], likes_count, liked);
}
function parseGeogroupStats(data) {
  let maxMembers = 0;
  const stats = {};
  data.forEach(({
    unit: strDate,
    count_manually_joined_members: nbMembers
  }) => {
    const date = parseStatsDate({
      strDate
    });
    if (date) {
      const strDate = date.format('l');
      stats[strDate] = {
        strDate,
        date,
        nbMembers
      };
      maxMembers = Math.max(maxMembers, nbMembers);
    }
  });
  return new GeogroupStats(maxMembers, Object.values(stats));
}
function parseActivityStats(data, computedRouteStats) {
  let totalDistance = 0;
  let totalHomeWorkDistance = 0;
  let totalDuration = 0;
  let totalHomeWorkDuration = 0;
  let totalNbRoutes = 0;
  let totalHomeWorkJourneys = 0;
  let maxActiveUsers = 0;
  let maxActiveUsersWithHomeWorkTrace = 0;
  let totalHomeWorkSavedCO2InKg = 0;
  const stats = {};
  data.forEach(({
    activity_day: strDate,
    counter_distance: distance,
    counter_home_work_reference_trips_distance: homeWorkDistance,
    counter_duration: duration,
    counter_home_work_reference_trips_duration: homeWorkDuration,
    counter_traces: nbRoutes,
    counter_home_work_reference_trips_traces: homeWorkJourneys,
    counter_active_user: nbActiveUsers,
    counter_active_user_with_home_work_trace: nbActiveUsersWithHomeWorkTrace,
    counter_home_work_reference_trips_co2_saved: homeWorkSavedCO2InKg,
    counter_employee_first_day_of_commuting: nbNewCommuters,
    counter_employee_first_day_of_cycling: nbNewCyclists
  }) => {
    const date = parseStatsDate({
      strDate
    });
    if (date) {
      const strDate = date.format('l');
      stats[strDate] = {
        strDate,
        date,
        nbComputedRoutes: 0,
        distance,
        homeWorkDistance,
        duration,
        homeWorkDuration,
        nbRoutes,
        homeWorkJourneys,
        nbActiveUsers,
        nbActiveUsersWithHomeWorkTrace,
        homeWorkSavedCO2InKg,
        nbNewCommuters,
        nbNewCyclists
      };
    }
    totalDistance += distance;
    totalHomeWorkDistance = totalHomeWorkDistance + (homeWorkDistance || 0);
    totalDuration += duration;
    totalHomeWorkDuration = totalHomeWorkDuration + (homeWorkDuration || 0);
    totalNbRoutes += nbRoutes;
    totalHomeWorkJourneys = totalHomeWorkJourneys + (homeWorkJourneys || 0);
    maxActiveUsers = Math.max(maxActiveUsers, nbActiveUsers || 0);
    maxActiveUsersWithHomeWorkTrace = Math.max(maxActiveUsersWithHomeWorkTrace, nbActiveUsersWithHomeWorkTrace || 0);
    totalHomeWorkSavedCO2InKg += homeWorkSavedCO2InKg || 0;
  });
  computedRouteStats?.data.forEach(({
    date,
    count
  }) => {
    const strDate = date.format('l');
    if (stats[strDate]) stats[strDate].nbComputedRoutes = count;else stats[strDate] = {
      strDate,
      date,
      nbComputedRoutes: count,
      distance: 0,
      homeWorkDistance: 0,
      duration: 0,
      homeWorkDuration: 0,
      nbRoutes: 0,
      homeWorkJourneys: 0,
      nbActiveUsers: 0,
      nbActiveUsersWithHomeWorkTrace: 0,
      homeWorkSavedCO2InKg: 0,
      nbNewCommuters: 0,
      nbNewCyclists: 0
    };
  });
  return new ActivityStats(computedRouteStats?.count || 0, totalDistance, totalDuration, totalNbRoutes, Object.values(stats), totalHomeWorkDistance, totalHomeWorkDuration, totalHomeWorkJourneys, totalHomeWorkSavedCO2InKg, maxActiveUsers, maxActiveUsersWithHomeWorkTrace);
}
function parseGeogroupSubgroupsStats({
  group_id: subgroupId,
  group_title: subgroupTitle,
  stats_counter_duration: duration,
  stats_counter_distance: distance,
  stats_counter_traces: journeys,
  stats_counter_home_work_reference_trips_duration: homeWorkDuration,
  stats_counter_home_work_reference_trips_distance: homeWorkDistance,
  stats_counter_home_work_reference_trips_traces: homeWorkJourneys,
  counter_active_user: activeMembers,
  counter_active_user_with_home_work_trace: activeMembersWithHomeWorkJourney
}) {
  return {
    subgroupId,
    subgroupTitle,
    duration,
    distance,
    journeys,
    homeWorkDuration,
    homeWorkDistance,
    homeWorkJourneys,
    activeMembers,
    activeMembersWithHomeWorkJourney
  };
}
function parseAverageJourneyStats({
  average_duration_in_seconds: averageDuration,
  average_distance_in_meters: averageDistance,
  duration_stats,
  distance_stats
}) {
  return {
    averageDuration,
    averageDistance,
    durationStats: duration_stats.map(({
      greater_than: min,
      less_than_or_equal: max,
      trace_count: count
    }) => ({
      min,
      max,
      count
    })),
    distanceStats: distance_stats.map(({
      greater_than: min,
      less_than_or_equal: max,
      trace_count: count
    }) => ({
      min,
      max,
      count
    }))
  };
}
function parseGeogroupStatement({
  id,
  activity_day,
  counter_distance,
  counter_traces
}) {
  return new GeogroupStatement(id, activity_day, Math.round((counter_distance || 0) / 100) / 10, counter_traces || 0);
}
function formatNewsPostContent(content) {
  function decodeHtmlEntity(str) {
    return str.replace(/&#(\d+);/g, function (match, dec) {
      return String.fromCharCode(dec);
    });
  }
  return decodeHtmlEntity(content).replaceAll('<strong>', '<b>').replaceAll('</strong>', '</b>').replaceAll('<p></p>', '<br/>').replaceAll('&nbsp;', ' ');
}